import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';
import { Button, Checkbox, Col, Divider, Flex, Form, Row, Tooltip } from 'antd';

import NotificationService from 'core/services/notification';

import { PrivateEquityIssuance, PrivateEquityShareholder } from '../models/privateEquityIssuance';
import { ServiceCost, VerificationPayload } from 'modules/main/actions/commonModels';
import DatePickerElement from 'components/datePicker/datePicker';
import dayjs from 'dayjs';
import { useDebouncedCallback } from 'use-debounce';
import { onCheckPrivateEquityLimit, onCreatePrivateEquityIssuanceWithFile, onUpdatePrivateEquityIssuanceWithFile } from '../services/privateEquityIssuance';
import Riyal from 'components/riyal/riyal';
import ActionCostComponent from 'modules/main/actions/cost';
import PrivateEquityIssuanceShareholdersTable from './PrivateEquityShareholdersTable';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import VerificationModal from 'modules/main/actions/verificationModal';
import { useNavigate, useParams } from 'react-router-dom';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import FileInput from 'components/fileInput/fileInput';
import FilePreview from 'components/filePreview/filePreview';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

interface PrivateEquityIssuanceFormProps {
	serviceCost?: ServiceCost;
	editMode?: boolean;
	privateEquityIssuance?: PrivateEquityIssuance;
}

const PrivateEquityIssuanceForm: FunctionComponent<PrivateEquityIssuanceFormProps> = ({ privateEquityIssuance, serviceCost, editMode }) => {
	const { t } = useTranslation();

	const { isGeneralSuspended } = useCompanySuspended();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();

	const [form] = Form.useForm();

	const totalNewSharesWatcher = Form.useWatch('total_new_common_shares', form);
	const ownersRegistryFileWatcher = Form.useWatch('owners_registry_file', form);
	const newCapitalDocumentWatcher = Form.useWatch('new_capital_document', form);
	const [shareholders, setShareholders] = useState<PrivateEquityShareholder[]>([]);

	const [checkingLimit, setCheckingLimit] = useState<boolean>(false);
	const [limitChecked, setLimitChecked] = useState<boolean>(false);

	const [acknowledge, setAcknowledge] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);

	useEffect(() => {
		if (privateEquityIssuance) {
			form.setFieldsValue({
				issuing_date: dayjs(privateEquityIssuance.action.issuing_date),
				total_new_common_shares: privateEquityIssuance.action.number_of_issued_units,
				issuing_price: privateEquityIssuance.action.issuing_price,
				new_capital_document: privateEquityIssuance.action.new_capital_document,
			});
		}
	}, [privateEquityIssuance]);

	const checkLimitHandler = useDebouncedCallback(async (value: number | null) => {
		try {
			if (value === null || value === 0) return;

			setCheckingLimit(true);

			await onCheckPrivateEquityLimit(value);

			setLimitChecked(true);
			setCheckingLimit(false);
		} catch (error: any) {
			setCheckingLimit(false);
			setLimitChecked(false);

			if (error?.status == 422) mapResponseErrors(error, form);
		}
	}, 800);

	const onSubmit = async (data: VerificationPayload) => {
		try {
			setIsSubmitting(true);

			const payload = new FormData();

			if (editMode) payload.append('_method', 'put');

			payload.append('otp', data.key);
			payload.append('issuing_date', dayjs(form.getFieldValue('issuing_date')).format('YYYY-MM-DD'));
			payload.append('total_new_common_shares', String(form.getFieldValue('total_new_common_shares')));
			payload.append('issuing_price', String(form.getFieldValue('issuing_price')));

			const newCapitalDocument = form.getFieldValue('new_capital_document');
			if (newCapitalDocument instanceof File) payload.append('new_capital_document', newCapitalDocument);

			shareholders.forEach((shareholder, index) => {
				payload.append(`owners_registry[${index}][name]`, shareholder.name);
				payload.append(`owners_registry[${index}][national_id]`, shareholder.national_id);
				payload.append(`owners_registry[${index}][phone_country_code]`, shareholder.phone_country_code);
				payload.append(`owners_registry[${index}][phone_number]`, shareholder.phone_number);
				payload.append(`owners_registry[${index}][owned_shares]`, String(shareholder.owned_shares));
			});

			editMode ? await onUpdatePrivateEquityIssuanceWithFile(id!, payload) : await onCreatePrivateEquityIssuanceWithFile(payload);

			NotificationService.success(
				t('COMMON.SUCCESS'),
				editMode ? t('ACTIONS.PRIVATE_EQUITY_ISSUANCE_UPDATED_SUCCESSFULLY') : t('ACTIONS.PRIVATE_EQUITY_ISSUANCE_CREATED_SUCCESSFULLY'),
			);
			navigate('/dashboard/actions/capitals');
		} catch (error: any) {
			setIsSubmitting(false);
			setVerificationModalOpen(false);

			if (error?.status === 422) mapResponseErrors(form, error);

			const message =
				error?.response?.data?.message ||
				(editMode ? t('ACTIONS.PRIVATE_EQUITY_ISSUANCE_NOT_UPDATED') : t('ACTIONS.PRIVATE_EQUITY_ISSUANCE_NOT_CREATED'));
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	return (
		<>
			<Form form={form}>
				<Row gutter={[16, 16]}>
					<Col span={24} md={8}>
						<DatePickerElement
							label={t('ACTIONS.ISSUING_DATE')}
							name="issuing_date"
							rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
							minDate={dayjs().add(2, 'day')}
						/>
					</Col>

					<Col span={24} md={8}>
						<FormNumberElement
							name="total_new_common_shares"
							min={1}
							label={t('ACTIONS.TOTAL_NEW_COMMON_SHARES')}
							rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
							addonAfter={<span className="me-2">{t('COMMON.SHARES')}</span>}
							onChange={(value) => checkLimitHandler(Number(value))}
							loading={checkingLimit}
						/>
					</Col>

					<Col span={24} md={8}>
						<FormNumberElement
							name="issuing_price"
							precision={2}
							label={t('ACTIONS.ISSUANCE_PRICE')}
							rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
							addonAfter={
								<span className="me-2">
									<Riyal style={{ marginTop: -4 }} /> / {t('ACTIONS.SHARE')}
								</span>
							}
						/>
					</Col>
				</Row>

				<Row gutter={[16, 16]} className="mt-3">
					<Col span={24} md={12}>
						<Form.Item
							name="new_capital_document"
							style={{ maxWidth: '100%' }}
							rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
							<FileInput
								accept="pdf"
								name={t('ACTIONS.NEW_CAPITAL_DOCUMENT')}
								onSelect={(file) => form.setFieldValue('new_capital_document', file)}
							/>
						</Form.Item>

						{typeof newCapitalDocumentWatcher === 'string' && (
							<FilePreview
								transparent
								name={t('ACTIONS.NEW_CAPITAL_DOCUMENT')}
								downloadLink={newCapitalDocumentWatcher}
								data={t('ACTIONS.CURRENTLY_UPLOADED_DOCUMENT')}
							/>
						)}
					</Col>
				</Row>

				{(limitChecked || editMode) && (
					<>
						<Divider />

						<PrivateEquityIssuanceShareholdersTable
							fileUploaded={!!ownersRegistryFileWatcher}
							totalNewShares={totalNewSharesWatcher}
							privateEquityIssuance={privateEquityIssuance!}
							onUpdateShareholders={(shareholders) => setShareholders(shareholders)}
						/>

						<Form.Item name="owners_registry_file" hidden noStyle />

						<Flex wrap gap={20} justify="space-between" align="center" className="mt-5">
							<Checkbox onChange={(event) => setAcknowledge(event.target.checked)}>
								<span className="fw-semibold d-block">{t('ACTIONS.AGREEMENT_TO_TERMS_PRIVATE_EQUITY')}</span>
								<span className="text-light">{t('MORTGAGE.AGREEMENT_2')}</span>
							</Checkbox>

							<Flex gap={20} align="center">
								{!editMode && <ActionCostComponent serviceCost={serviceCost!} />}

								<Tooltip
									color="red"
									title={
										isGeneralSuspended
											? t('COMMON.COMPANY_GENERAL_SUSPENDED')
											: ownersRegistryFileWatcher
												? ''
												: totalNewSharesWatcher - shareholders.reduce((acc, shareholder) => acc + shareholder.owned_shares, 0) != 0
													? t('ACTIONS.STILL_HAVE_SHARES_TO_ISSUE', {
															remaining: totalNewSharesWatcher - shareholders.reduce((acc, shareholder) => acc + shareholder.owned_shares, 0),
														})
													: ''
									}>
									<Button
										type="primary"
										className="px-4"
										disabled={
											isGeneralSuspended ||
											!acknowledge ||
											(totalNewSharesWatcher - shareholders.reduce((acc, shareholder) => acc + shareholder.owned_shares, 0) != 0 &&
												!ownersRegistryFileWatcher)
										}
										onClick={async () => {
											try {
												await form.validateFields();
												setVerificationModalOpen(true);
											} catch (error) {}
										}}>
										{editMode ? t('COMMON.UPDATE') : t('COMMON.EXECUTE')}
									</Button>
								</Tooltip>
							</Flex>
						</Flex>
					</>
				)}
			</Form>

			<VerificationModal
				otpOnly
				open={verificationModalOpen}
				isLoading={isSubmitting}
				className="confirmationModal"
				onCancel={() => setVerificationModalOpen(false)}
				onVerificationSuccess={onSubmit}
				module={editMode ? 'update_capitalRaise_private_equity_issuance' : 'create_capitalRaise_private_equity_issuance'}
				note={t('ACTIONS.PRIVATE_EQUITY_ISSUANCE_NOTE')}
			/>
		</>
	);
};

export default PrivateEquityIssuanceForm;
